import { useEffect, useRef } from "react";
import axios from "axios";

import { useAppSelector } from "./hooks";
import { getAppsFlyerWebUID, isInReactNativeWebView } from "../lib/appsflyer";

const API_URL = process.env.REACT_APP_API_URL;

type NativeAppsFlyerIdEvent = CustomEvent<{
  appsflyerId: string;
  platform: "ios" | "android";
}>;

/**
 * Registers this device/browser's AppsFlyer identifier against the signed-in
 * user, so the backend can attribute server-side purchase conversion events
 * (dispatched from UserTransactionController::walletJobCallback) to the
 * right AppsFlyer install.
 *
 * In-app: the id comes from the native shell as `window.__nativeAppsFlyerId`
 * plus a `native-appsflyer-id` event. Standalone web: it comes from the
 * AppsFlyer Web SDK once it resolves.
 */
export const useAppsFlyerIdentity = (): void => {
  const authToken = useAppSelector((state) => state.auth.token);

  const identifierRef = useRef<string | null>(
    typeof window !== "undefined" ? window.__nativeAppsFlyerId ?? null : null,
  );
  const platformRef = useRef<"ios" | "android" | "web">(
    isInReactNativeWebView() ? (window.__nativePlatform ?? "android") : "web",
  );
  const registeredRef = useRef<string | null>(null);

  useEffect(() => {
    const register = async () => {
      const appsflyerId = identifierRef.current;
      if (!appsflyerId || !authToken) return;

      const key = `${authToken}:${appsflyerId}`;
      if (registeredRef.current === key) return;

      try {
        await axios.post(
          `${API_URL}/user/appsflyer-id`,
          { appsflyer_id: appsflyerId, platform: platformRef.current },
          { headers: { Authorization: `Bearer ${authToken}` } },
        );
        registeredRef.current = key;
      } catch {
        // Best effort: analytics attribution should never block the UI.
      }
    };

    const onNativeAppsFlyerId = (event: Event) => {
      const { appsflyerId, platform } =
        (event as NativeAppsFlyerIdEvent).detail ?? {};
      if (!appsflyerId) return;
      identifierRef.current = appsflyerId;
      if (platform) platformRef.current = platform;
      void register();
    };

    window.addEventListener("native-appsflyer-id", onNativeAppsFlyerId);

    if (!identifierRef.current) {
      if (window.__nativeAppsFlyerId) {
        identifierRef.current = window.__nativeAppsFlyerId;
      } else if (!isInReactNativeWebView()) {
        getAppsFlyerWebUID().then((uid) => {
          if (!uid) return;
          identifierRef.current = uid;
          void register();
        });
      }
    }
    void register();

    return () => {
      window.removeEventListener("native-appsflyer-id", onNativeAppsFlyerId);
    };
  }, [authToken]);
};
